/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { ModifyKeepUntilActionKernel } from 'client-code/actions';
import { KeepUntilOptionItem } from 'client-code/recordings';

import { NdvrInteractor } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { KeyManager, ScreenTaskControllerTypes } from '../../../core/navigation';
import type { TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { TAction, TOverlayOptions } from '../../../core/types';
import { UIOrchestrator, UIOrchestratorState } from '../../../core/uiorchestrator';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';

type keepUntilRecordingOverlayParams =
    | { for: 'recordingId'; action: TAction }
    | { for: 'offerId'; action: TAction };

// eslint-disable-next-line no-shadow
enum keepUntilRecordingOverlayOptions {
    CANCEL,
    KEEP_UNTIL,
}

const TAG = 'KeepUntilRecordingOverlayReducers';

// eslint-disable-next-line no-unused-vars
const prepareKeepUntilRecordingOverlayForOfferId = (action: any) => async (dispatch: Function) => {
    // creating a placeholder for implementation which will be done in future
    // as of now, we are not implementing this feature
    dispatch(showWhisper({ message: Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE) }));
    if (KeyManager.getInstance().isKeyIgnoring()) KeyManager.getInstance().stopKeyIgnoring();
};

const prepareKeepUntilRecordingOverlayForRecordingId =
    (action: any) => async (dispatch: Function) => {
        try {
            const { contentId, recordingId } = action.kernel as ModifyKeepUntilActionKernel;
            const services = new NdvrInteractor();
            // call to get default recording options
            // schedule call is mandatory before options to get the available options
            const defaultRecordingOptions = services.getRecordingScheduleRequestModel(
                contentId,
                recordingId,
            );
            await defaultRecordingOptions.send();
            const recordingOptionsService = services.getRecordingOptionsRequestModel(
                contentId,
                recordingId,
            );
            const recordingOptions = await recordingOptionsService.send();
            const { keepUntil } = recordingOptions.recordingOptions;
            const overlayOptions: TOverlayOptions[] =
                keepUntil?.optionList?.map((option: KeepUntilOptionItem) => {
                    return {
                        text: option.label!,
                        value: {
                            type: keepUntilRecordingOverlayOptions.KEEP_UNTIL,
                            keepUntil: option.keepUntil!,
                        },
                    };
                }) || [];
            if (overlayOptions.length !== 0) {
                overlayOptions.push({
                    text: Locale.getString(LocaleKeys.KURO_OPTION_CANCEL),
                    value: { type: keepUntilRecordingOverlayOptions.CANCEL },
                });
                const overlayDialogProps: IOverlayDialog = {
                    hasDimmer: true,
                    hasSeperator: false,
                    mouseTrapValue: TAG,
                    options: overlayOptions,
                    title: Locale.getString(LocaleKeys.KURO_TITLE),
                };
                dispatch(
                    setScreenTaskController(ScreenTaskControllerTypes.KEEP_UNTIL_RECORDING_OVERLAY),
                );
                dispatch(showOverlayDialog(overlayDialogProps));
            }
        } catch (error) {
            Log.e(TAG, 'Error while preparing Keep Until Recording Overlay', error);
            dispatch(
                showWhisper({ message: Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE) }),
            );
        } finally {
            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
        }
    };

export const prepareKeepUntilRecordingOverlay =
    (params: keepUntilRecordingOverlayParams) => async (dispatch: Function) => {
        KeyManager.getInstance().startKeyIgnoring();
        if (params.for === 'recordingId') {
            dispatch(prepareKeepUntilRecordingOverlayForRecordingId(params.action));
        } else {
            dispatch(prepareKeepUntilRecordingOverlayForOfferId(params.action));
        }
    };

export const onKeyOkKeepUntilRecordingOverlay =
    () => async (dispatch: Function, getState: TGetState) => {
        const { overlayHighlight, overlayDialog, programScreen } = getState();
        const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
        const { uiProperties } = overlayDialog;
        const { options } = uiProperties;
        const selectedOption = options[overlayHighlightVerticalPosition];

        if (selectedOption.value.type !== keepUntilRecordingOverlayOptions.KEEP_UNTIL) {
            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
            dispatch(hideOverlayDialog());
            return;
        }

        if (!KeyManager.getInstance().isKeyIgnoring()) {
            KeyManager.getInstance().startKeyIgnoring();
        }
        const { currentHighlightIndex, horizontalOptions } = programScreen;
        const action = horizontalOptions[currentHighlightIndex].identifier;
        const { contentId, recordingId } = action.kernel as ModifyKeepUntilActionKernel;
        const services = new NdvrInteractor();
        const recordingDisplayOptionsService = services.getRecordingScheduleRequestModel(
            contentId,
            recordingId,
        );
        try {
            const recordingDisplayOptions = await recordingDisplayOptionsService.send();

            recordingDisplayOptions.recordingValues.keepUntil = selectedOption.value.keepUntil;
            await services
                .getRequestModelToUpdateRecordingSchedule(
                    contentId,
                    recordingId,
                    recordingDisplayOptions,
                )
                .send();
            UIOrchestrator.stateChangeListener.notify(
                UIOrchestratorState.RECORDING_PERSISTENCE_MODIFIED,
            );
        } catch (error) {
            Log.e(TAG, 'Error while updating Keep Until Recording', error);
            dispatch(
                showWhisper({ message: Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE) }),
            );
        } finally {
            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
            dispatch(hideOverlayDialog());
        }
    };

export const onMouseClickKeepUntilRecordingOverlay =
    () => (dispatch: Function, getState: TGetState) => {
        const { value } = getState().inputFactory;

        if (value === TAG) {
            dispatch(hideOverlayDialog());
            return;
        }

        dispatch(onKeyOkKeepUntilRecordingOverlay());
    };
